import { PropsWithChildren, useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import useProjectData from 'hooks/useProjectData';
import { useAppContext } from 'providers/AppProvider';
import { selectCurrentProject, updateCurrentProject } from 'redux-slices/current-projects-slice';

/**
 * Pages under /projects/:projectId work on the current project, so select the project in the URL before
 * rendering them. A project that doesn't exist or isn't accessible shows a message instead.
 */
const ProjectGuard = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const urlProjectId = Number(projectId);
  const dispatch = useAppDispatch();
  const { setCurrentProjectId } = useAppContext();
  const { fetchProject } = useProjectData();
  const currentProject = useAppSelector(selectCurrentProject);
  const [notFoundId, setNotFoundId] = useState<number | null>(null);
  const isSelected = currentProject?.id === urlProjectId;

  useEffect(() => {
    if (isSelected) return;
    fetchProject(urlProjectId)
      .then(project => {
        dispatch(updateCurrentProject(project));
        setCurrentProjectId(project.id);
      })
      .catch(() => setNotFoundId(urlProjectId));
  }, [urlProjectId, isSelected]);

  if (isSelected) return <>{children}</>;

  return notFoundId === urlProjectId ? (
    <Alert variant="danger" className="bg-body text-body border-0">
      {t('projectNotFound')}
    </Alert>
  ) : (
    <Alert variant="info" className="bg-body text-body border-0">
      {t('loading')}
    </Alert>
  );
};

export default ProjectGuard;
